import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate, useParams, Navigate } from 'react-router-dom'
import moment from 'moment'

import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'
import {
  checkPermission,
  formatCurrency,
  formatString,
  createWorkbook,
  saveWorkbook,
  addWorksheet,
  setHeaders,
  setColumnsAndRows,
  DEFAULTNUMHEADERS,
  dateFormat,
  currencyFormat,
  numberFormat,
} from '../../utilities/helpers'
import { ORGANIZATION_PERMISSION_SLUGS } from '@blocktickets/shared-utils'
import {
  chargePaymentPlanNextInstallment,
  customerPaymentMethodsByCustomerId,
  getOrganizationPaymentPlans,
  payPaymentPlanRemainingBalance,
} from '../../utilities/api'
import { useModal } from '../../providers/ModalProvider/ModalProvider'
import {
  CustomerPaymentMethodsTable,
  parsePaymentMethodsResponse,
  useCustomerPaymentMethods,
} from '../../components/CustomerPaymentMethods'

import Card from 'react-bootstrap/Card'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Modal from 'react-bootstrap/Modal'
import Alert from 'react-bootstrap/Alert'

import { NoPermissionsContainer } from '../../components/NoPermissionsContainer'
import { PageLoadingContainer } from '../../components/PageLoadingContainer'
import { Spinner } from '../../components/LoadingContainer/Spinner'

const EMPTY_PLANS = []

const TUTORIAL_VIDEO_SOURCE =
  'https://blocktickets.nyc3.cdn.digitaloceanspaces.com/tutorials/Payment%20Plans_1080p.mp4'

const formatDate = (value) => {
  if (!value) return 'N/A'
  const date = moment(value)
  return date.isValid() ? date.format('MM/DD/YYYY') : 'N/A'
}

const statusLabel = (value) => formatString(value || 'open')
const formatCurrencyOrNA = (value) =>
  value == null ? 'N/A' : formatCurrency(value)
const BADGE_STATUS_MAP = {
  refunded: 'refunded',
  past_due: 'past_due',
  incomplete: 'incomplete',
  failed: 'failed',
  paid: 'paid',
  complete: 'complete',
  scheduled: 'scheduled',
  reserved: 'reserved',
  pending: 'pending',
  pending_send: 'pending_send',
  active: 'active',
  open: 'open',
  plan: 'plan',
}
const statusBadgeClass = (value) => {
  const normalized = String(value || 'open')
    .toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_')
  const badgeStatus = BADGE_STATUS_MAP[normalized] || 'primary'
  return `badge-sm badge--${badgeStatus} w-max-content`
}
const getInstallmentStatusLabel = (installmentStatus, planIsActive) => {
  const normalized = String(installmentStatus || '').toLowerCase()
  if (planIsActive === false && normalized === 'scheduled') {
    return 'cancelled'
  }
  return installmentStatus
}
const getInstallmentDueDate = (dueDate, installmentStatus, planIsActive) => {
  const normalized = String(installmentStatus || '').toLowerCase()
  if (planIsActive === false && normalized === 'scheduled') {
    return null
  }
  return dueDate
}
function PaymentPlanChargeConfirmModal({
  title,
  description,
  confirmLabel,
  onConfirm,
  closeModal,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleConfirm = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      await onConfirm()
      closeModal()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal.Body>
      <div className="modal-body-heading">
        <h1 className="modal-body-heading-title">{title}</h1>
        <p>{description}</p>
      </div>
      <Stack className="btn-group-flex">
        <Button size="lg" disabled={isSubmitting} onClick={handleConfirm}>
          {isSubmitting ? <Spinner variant="white" /> : confirmLabel}
        </Button>
        <Button
          size="lg"
          variant="outline-light"
          onClick={closeModal}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
      </Stack>
    </Modal.Body>
  )
}

export default function PaymentPlansReportPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { orgPermissions } = useContext(UserContext)
  const { openModal, closeModal } = useModal()
  const { getPermissions } = AuthService

  const orgPermissionsReady = orgPermissions?.length > 0
  const organization = AuthService.getOrg()?.[0]
  const paymentPlansEnabled = Boolean(organization?.setting?.paymentPlans)
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_BALANCE_PAYOUTS
    )

  const [isLoading, setIsLoading] = useState(true)
  const [paymentPlans, setPaymentPlans] = useState(EMPTY_PLANS)
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([])
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false)
  const [paymentMethodsError, setPaymentMethodsError] = useState('')
  const [manualChargeError, setManualChargeError] = useState('')
  const [manualChargeSuccess, setManualChargeSuccess] = useState('')
  const [isChargingNextInstallment, setIsChargingNextInstallment] =
    useState(false)
  const [isPayingRemainingBalance, setIsPayingRemainingBalance] =
    useState(false)
  const [showTutorial, setShowTutorial] = useState(false)
  const tutorialVideoRef = useRef(null)

  const handleCloseTutorial = () => {
    if (tutorialVideoRef.current) {
      tutorialVideoRef.current.pause()
      tutorialVideoRef.current.currentTime = 0
    }
    setShowTutorial(false)
  }

  const activeOrgUuid = organization?.uuid
  const isProductionStage = process.env.REACT_APP_STAGE === 'production'
  const expectedConnectedAccountId = isProductionStage
    ? organization?.connected_account_id
    : organization?.test_connected_account_id
  const selectedPlan = useMemo(
    () => paymentPlans.find((plan) => String(plan?.id) === String(id)),
    [id, paymentPlans]
  )
  const isPaymentPlanEnvironmentMismatch = Boolean(
    selectedPlan?.connectedAccountId &&
    expectedConnectedAccountId &&
    String(selectedPlan.connectedAccountId) !==
      String(expectedConnectedAccountId)
  )
  const canManagePaymentMethods = Boolean(
    id &&
    selectedPlan?.stripeCustomerId &&
    hasPermission &&
    !isPaymentPlanEnvironmentMismatch
  )
  const hasScheduledInstallments = Boolean(
    (selectedPlan?.installments || []).some(
      (installment) =>
        String(installment?.status || '').toLowerCase() === 'scheduled'
    )
  )
  const hasRemainingBalance = Number(selectedPlan?.amountRemaining || 0) > 0
  const canRunManualChargeActions = Boolean(
    id &&
    selectedPlan?.stripeCustomerId &&
    hasPermission &&
    !isPaymentPlanEnvironmentMismatch &&
    selectedPlan?.active !== false &&
    String(selectedPlan?.status || '').toLowerCase() !== 'completed'
  )
  const loadPaymentMethods = useCallback(async () => {
    if (!selectedPlan?.stripeCustomerId) {
      setSavedPaymentMethods([])
      setPaymentMethodsError('')
      return
    }
    setIsLoadingPaymentMethods(true)
    setPaymentMethodsError('')
    try {
      const res = await customerPaymentMethodsByCustomerId(
        selectedPlan?.stripeCustomerId,
        selectedPlan?.connectedAccountId
      )
      const { paymentMethods } = parsePaymentMethodsResponse(res)
      setSavedPaymentMethods(paymentMethods)
    } catch (err) {
      setSavedPaymentMethods([])
      setPaymentMethodsError(
        err?.response?.data?.error?.message ||
          err?.message ||
          'Unable to load payment methods'
      )
    } finally {
      setIsLoadingPaymentMethods(false)
    }
  }, [selectedPlan?.stripeCustomerId, selectedPlan?.connectedAccountId])

  const loadPaymentPlans = useCallback(async () => {
    if (!hasPermission || !activeOrgUuid) {
      setPaymentPlans(EMPTY_PLANS)
      return
    }
    setIsLoading(true)
    try {
      const res = await getOrganizationPaymentPlans(activeOrgUuid)
      setPaymentPlans(res?.data?.paymentPlans || EMPTY_PLANS)
    } catch (err) {
      console.error(err)
      setPaymentPlans(EMPTY_PLANS)
    } finally {
      setIsLoading(false)
    }
  }, [hasPermission, activeOrgUuid])

  const {
    processingPaymentMethodId,
    paymentMethodsError: paymentMethodActionError,
    handleSetDefaultPaymentMethod,
    openReplacePaymentMethodModal,
    openDeletePaymentMethodModal,
  } = useCustomerPaymentMethods({
    stripeCustomerId: selectedPlan?.stripeCustomerId,
    connectAccountId: selectedPlan?.connectedAccountId,
    preventLastPaymentMethod: selectedPlan?.active !== false,
    deleteConfirmText:
      'This card will no longer be saved for future payment plan charges.',
    openModal,
    closeModal,
    onReload: loadPaymentMethods,
  })

  const runManualChargeAction = useCallback(
    async ({ actionType }) => {
      if (!selectedPlan?.id || !activeOrgUuid) return
      setManualChargeError('')
      setManualChargeSuccess('')
      const runAction =
        actionType === 'remaining'
          ? payPaymentPlanRemainingBalance
          : chargePaymentPlanNextInstallment
      const setIsRunning =
        actionType === 'remaining'
          ? setIsPayingRemainingBalance
          : setIsChargingNextInstallment

      setIsRunning(true)
      try {
        await runAction(selectedPlan.id, {
          organizationUUID: activeOrgUuid,
        })
        setManualChargeSuccess(
          actionType === 'remaining'
            ? 'Remaining balance charged successfully.'
            : 'Next installment charged successfully.'
        )
        await loadPaymentPlans()
      } catch (err) {
        setManualChargeError(
          err?.response?.data?.error?.message ||
            err?.response?.data?.error ||
            err?.message ||
            'Unable to complete payment-plan charge action'
        )
      } finally {
        setIsRunning(false)
      }
    },
    [activeOrgUuid, loadPaymentPlans, selectedPlan?.id]
  )

  const openManualChargeModal = useCallback(
    (actionType) => {
      if (!selectedPlan?.id) return
      const isRemaining = actionType === 'remaining'
      openModal(
        PaymentPlanChargeConfirmModal,
        {
          title: isRemaining
            ? 'Charge remaining balance now?'
            : 'Charge next installment now?',
          description: isRemaining
            ? `This charges ${formatCurrencyOrNA(selectedPlan?.amountRemaining)} to the default card on file and marks all remaining installments as paid.`
            : `This charges ${formatCurrencyOrNA(selectedPlan?.nextAmount)} to the default card on file immediately.`,
          confirmLabel: isRemaining
            ? 'Charge Remaining Balance'
            : 'Charge Next Installment',
          onConfirm: () => runManualChargeAction({ actionType }),
          closeModal,
        },
        { className: 'modal--lg' }
      )
    },
    [
      closeModal,
      openModal,
      runManualChargeAction,
      selectedPlan?.amountRemaining,
      selectedPlan?.id,
      selectedPlan?.nextAmount,
    ]
  )

  const handleExcelExport = async (plansToExport = []) => {
    if (!plansToExport.length) return

    const workbook = createWorkbook()
    const worksheet = addWorksheet('Payment Plans', workbook)
    const generatedOn = moment().format('MMM Do YYYY, h:mm A')

    const formatPlan = (plan) => ({
      order_id: plan?.orderId || 'N/A',
      customer_name: plan?.customerName || 'N/A',
      email: plan?.email || 'N/A',
      order_date: plan?.orderDate || null,
      plan_total: Number(plan?.planTotal || 0),
      total_installments: Number(plan?.totalInstallments || 0),
      installments_paid: Number(plan?.installmentsPaid || 0),
      installments_remaining: Number(plan?.installmentsRemaining || 0),
      next_amount:
        plan?.nextAmount == null ? '' : Number(plan?.nextAmount || 0),
      amount_paid: Number(plan?.amountPaid || 0),
      amount_remaining:
        plan?.amountRemaining == null ? '' : Number(plan?.amountRemaining || 0),
      start_date: plan?.startDate || null,
      next_charge_at: plan?.nextChargeAt || null,
      end_date: plan?.endDate || null,
      status: formatString(plan?.status || 'open'),
    })

    const headers = [
      `Payment plans: ${plansToExport.length}`,
      `Generated on: ${generatedOn}`,
    ]

    setHeaders('Payment Plans Report', organization?.name, worksheet, headers)
    setColumnsAndRows(
      Object.keys(formatPlan(plansToExport[0])),
      Object.values(plansToExport),
      formatPlan,
      worksheet
    )

    const headerRows = DEFAULTNUMHEADERS + headers.length
    dateFormat(4, worksheet, headerRows)
    currencyFormat(5, worksheet, headerRows)
    numberFormat(6, worksheet, headerRows)
    numberFormat(7, worksheet, headerRows)
    numberFormat(8, worksheet, headerRows)
    currencyFormat(9, worksheet, headerRows)
    currencyFormat(10, worksheet, headerRows)
    currencyFormat(11, worksheet, headerRows)
    dateFormat(12, worksheet, headerRows)
    dateFormat(13, worksheet, headerRows)
    dateFormat(14, worksheet, headerRows)

    // For a single plan drill-down export, include installment rows in a second sheet.
    if (plansToExport.length === 1) {
      const plan = plansToExport[0]
      const installments = plan?.installments || []
      if (installments.length) {
        const installmentSheet = addWorksheet('Installments', workbook)
        const formatInstallment = (installment) => ({
          due_date: installment?.dueDate || null,
          amount: Number(installment?.amount || 0),
          status: formatString(installment?.status || 'scheduled'),
        })
        const installmentHeaders = [
          `Order: ${plan?.orderId || 'N/A'}`,
          `Customer: ${plan?.customerName || 'N/A'}`,
        ]

        setHeaders(
          'Payment Plan Installments',
          organization?.name,
          installmentSheet,
          installmentHeaders
        )
        setColumnsAndRows(
          Object.keys(formatInstallment(installments[0])),
          Object.values(installments),
          formatInstallment,
          installmentSheet
        )

        const installmentHeaderRows =
          DEFAULTNUMHEADERS + installmentHeaders.length
        dateFormat(1, installmentSheet, installmentHeaderRows)
        currencyFormat(2, installmentSheet, installmentHeaderRows)
      }
    }

    saveWorkbook(workbook, 'payment-plans-report')
      .then()
      .catch((err) => alert(err.message))
  }

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission || !activeOrgUuid) {
      setIsLoading(false)
      return
    }
    loadPaymentPlans()
  }, [orgPermissionsReady, hasPermission, activeOrgUuid, loadPaymentPlans])

  useEffect(() => {
    if (!canManagePaymentMethods) return
    loadPaymentMethods()
  }, [canManagePaymentMethods, loadPaymentMethods])

  if (!orgPermissionsReady || isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!paymentPlansEnabled) {
    return <Navigate to="/reporting" replace />
  }

  if (!hasPermission) {
    return <NoPermissionsContainer />
  }

  if (id && !selectedPlan) {
    return (
      <div className="spacer-sm">
        <Card body>
          <Stack gap={3}>
            <Card.Title as="h2">Payment Plan Not Found</Card.Title>
            <p className="m-0">No payment plan exists for this identifier.</p>
            <Button
              variant="outline-light"
              className="w-max-content"
              onClick={() => navigate('/payment-plans-report')}
            >
              Back to Payment Plans
            </Button>
          </Stack>
        </Card>
      </div>
    )
  }

  return (
    <section className="spacer-sm" data-testid="payment-plans-report-section">
      {!id ? (
        <>
          <header className="section-header-sm">
            <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
              <div className="section-heading section-heading--secondary d-flex align-items-center gap-3">
                <h1 className="mb-0">Payment Plans Report</h1>
              </div>
              <Button
                variant="outline-light"
                className="icon-button btn-download"
                onClick={() => handleExcelExport(paymentPlans)}
                disabled={!paymentPlans.length}
              >
                Export Payment Plans
              </Button>
            </div>
            <p className="section-header-desc">
              View all payment plans for this organization and drill into each
              plan&apos;s installment timeline.
            </p>
            <p className="section-header-desc mt-2">
              <strong>Note: </strong>
              <Button
                variant="link"
                className="p-0 align-baseline"
                onClick={() => setShowTutorial(true)}
              >
                View Tutorial
              </Button>
            </p>
          </header>

          <Modal
            show={showTutorial}
            onHide={handleCloseTutorial}
            size="lg"
            centered
          >
            <Modal.Header closeButton>
              <Modal.Title>Payment Plans Tutorial</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <video
                ref={tutorialVideoRef}
                controls
                autoPlay
                style={{ width: '100%' }}
              >
                <source src={TUTORIAL_VIDEO_SOURCE} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </Modal.Body>
          </Modal>

          <Card body>
            <div className="table-container">
              <Table responsive>
                <thead>
                  <tr className="md md--scroll">
                    <th className="text-left uppercase text-body">Order id</th>
                    <th className="text-left uppercase text-body">Customer</th>
                    <th className="text-left uppercase text-body">Email</th>
                    <th className="text-left uppercase text-body">
                      Plan total
                    </th>
                    <th className="text-left uppercase text-body">
                      Amount paid
                    </th>
                    <th className="text-left uppercase text-body">
                      Amount remaining
                    </th>
                    <th className="text-left uppercase text-body">
                      Installments
                    </th>
                    <th className="text-left uppercase text-body">
                      Next amount
                    </th>
                    <th className="text-left uppercase text-body">
                      Next charge
                    </th>
                    <th className="text-left uppercase text-body">Status</th>
                    <th className="text-left uppercase text-body">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {paymentPlans.map((plan) => (
                    <tr className="md-x md-x--scroll" key={plan.id}>
                      <td className="text-left">{plan.orderId || 'N/A'}</td>
                      <td className="text-left">
                        {plan.customerName || 'N/A'}
                      </td>
                      <td className="text-left">{plan.email || 'N/A'}</td>
                      <td className="text-left">
                        {formatCurrency(plan.planTotal)}
                      </td>
                      <td className="text-left">
                        {formatCurrency(plan.amountPaid)}
                      </td>
                      <td className="text-left">
                        {formatCurrencyOrNA(plan.amountRemaining)}
                      </td>
                      <td className="text-left">
                        {plan.installmentsPaid}/{plan.totalInstallments}
                      </td>
                      <td className="text-left">
                        {formatCurrencyOrNA(plan.nextAmount)}
                      </td>
                      <td className="text-left">
                        {formatDate(plan.nextChargeAt)}
                      </td>
                      <td className="text-left">
                        <Badge
                          bg="none"
                          className={statusBadgeClass(plan.status)}
                        >
                          {statusLabel(plan.status)}
                        </Badge>
                      </td>
                      <td className="text-left">
                        <Button
                          variant="outline-light"
                          size="sm"
                          onClick={() =>
                            navigate(
                              `/payment-plans-report/payment-plans/${plan.id}`
                            )
                          }
                        >
                          View
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {paymentPlans.length === 0 && (
                    <tr className="md-x md-x--scroll">
                      <td colSpan={12} className="text-center">
                        No payment plans found for this organization.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      ) : (
        <Card body>
          <Stack gap={4}>
            <Stack direction="horizontal" className="justify-content-between">
              <Stack gap={2}>
                <Card.Title as="h2">Payment Plan</Card.Title>
                <Stack direction="horizontal" gap={2}>
                  <span className="subtitle subtitle-bolder m-0">
                    Order {selectedPlan.orderId || 'N/A'}
                  </span>
                  <Badge
                    bg="none"
                    className={statusBadgeClass(selectedPlan.status)}
                  >
                    {statusLabel(selectedPlan.status)}
                  </Badge>
                </Stack>
              </Stack>
              <Button
                variant="outline-light"
                onClick={() => navigate('/payment-plans-report')}
              >
                Back to Payment Plans
              </Button>
            </Stack>
            <div>
              <Button
                variant="outline-light"
                className="icon-button btn-download"
                onClick={() => handleExcelExport([selectedPlan])}
              >
                Export Payment Plan
              </Button>
            </div>

            <Table responsive>
              <thead>
                <tr className="md md--scroll">
                  <th className="text-left uppercase text-body">Customer</th>
                  <th className="text-left uppercase text-body">Email</th>
                  <th className="text-left uppercase text-body">Order date</th>
                  <th className="text-left uppercase text-body">Plan total</th>
                  <th className="text-left uppercase text-body">Amount paid</th>
                  <th className="text-left uppercase text-body">
                    Amount remaining
                  </th>
                  <th className="text-left uppercase text-body">Start date</th>
                  <th className="text-left uppercase text-body">Next charge</th>
                  <th className="text-left uppercase text-body">End date</th>
                </tr>
              </thead>
              <tbody>
                <tr className="md-x md-x--scroll">
                  <td className="text-left">
                    {selectedPlan.customerName || 'N/A'}
                  </td>
                  <td className="text-left">{selectedPlan.email || 'N/A'}</td>
                  <td className="text-left">
                    {formatDate(selectedPlan.orderDate)}
                  </td>
                  <td className="text-left">
                    {formatCurrency(selectedPlan.planTotal)}
                  </td>
                  <td className="text-left">
                    {formatCurrency(selectedPlan.amountPaid)}
                  </td>
                  <td className="text-left">
                    {formatCurrencyOrNA(selectedPlan.amountRemaining)}
                  </td>
                  <td className="text-left">
                    {formatDate(selectedPlan.startDate)}
                  </td>
                  <td className="text-left">
                    {formatDate(selectedPlan.nextChargeAt)}
                  </td>
                  <td className="text-left">
                    {formatDate(selectedPlan.endDate)}
                  </td>
                </tr>
              </tbody>
            </Table>

            <Card body className="card--light card-with-border">
              <Stack gap={3}>
                <Stack
                  direction="horizontal"
                  className="justify-content-between align-items-start gap-3"
                >
                  <div>
                    <Card.Title as="h3" className="mb-1">
                      Payment methods
                    </Card.Title>
                    <p className="small text-muted m-0">
                      Update cards saved to this Stripe customer for future
                      installment charges.
                    </p>
                  </div>
                </Stack>

                {isPaymentPlanEnvironmentMismatch ? (
                  <Alert variant="warning" className="mb-0">
                    Payment methods are disabled because this payment plan was
                    created in a different Stripe environment than the current
                    app session.
                  </Alert>
                ) : null}

                {paymentMethodsError || paymentMethodActionError ? (
                  <Alert variant="danger" className="mb-0">
                    {paymentMethodsError || paymentMethodActionError}
                  </Alert>
                ) : null}

                {isPaymentPlanEnvironmentMismatch ? null : !selectedPlan?.stripeCustomerId ? (
                  <Alert variant="warning" className="mb-0">
                    Stripe customer is unavailable for this payment plan.
                  </Alert>
                ) : isLoadingPaymentMethods ? (
                  <Stack className="align-items-center py-3">
                    <Spinner />
                  </Stack>
                ) : savedPaymentMethods.length > 0 ? (
                  <CustomerPaymentMethodsTable
                    savedPaymentMethods={savedPaymentMethods}
                    processingPaymentMethodId={processingPaymentMethodId}
                    preventLastPaymentMethod={selectedPlan?.active !== false}
                    onSetDefault={handleSetDefaultPaymentMethod}
                    onUpdateCard={openReplacePaymentMethodModal}
                    onRemoveCard={openDeletePaymentMethodModal}
                  />
                ) : (
                  <Alert variant="secondary" className="mb-0">
                    No saved card was returned for this payment plan customer.
                    This is unexpected because payment plans save a card
                    automatically. Please refresh and try again.
                  </Alert>
                )}
              </Stack>
            </Card>

            {manualChargeError ? (
              <Alert variant="danger" className="mb-0">
                {manualChargeError}
              </Alert>
            ) : null}
            {manualChargeSuccess ? (
              <Alert variant="success" className="mb-0">
                {manualChargeSuccess}
              </Alert>
            ) : null}

            <Stack
              direction="horizontal"
              className="justify-content-between align-items-start gap-3"
            >
              <Card.Title as="h3" className="mb-0">
                Installments
              </Card.Title>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <Button
                  variant="outline-light"
                  size="sm"
                  disabled={
                    !canRunManualChargeActions ||
                    !hasScheduledInstallments ||
                    isChargingNextInstallment ||
                    isPayingRemainingBalance
                  }
                  onClick={() => openManualChargeModal('next')}
                >
                  {isChargingNextInstallment ? (
                    <Spinner variant="dark" />
                  ) : (
                    'Charge Next Installment Now'
                  )}
                </Button>
                <Button
                  variant="outline-light"
                  size="sm"
                  disabled={
                    !canRunManualChargeActions ||
                    !hasRemainingBalance ||
                    !hasScheduledInstallments ||
                    isChargingNextInstallment ||
                    isPayingRemainingBalance
                  }
                  onClick={() => openManualChargeModal('remaining')}
                >
                  {isPayingRemainingBalance ? (
                    <Spinner variant="dark" />
                  ) : (
                    'Pay Remaining Balance Now'
                  )}
                </Button>
              </Stack>
            </Stack>
            <div className="table-container">
              <Table responsive>
                <thead>
                  <tr className="md md--scroll">
                    <th className="text-left uppercase text-body">#</th>
                    <th className="text-left uppercase text-body">Due date</th>
                    <th className="text-left uppercase text-body">Amount</th>
                    <th className="text-left uppercase text-body">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedPlan.installments || []).map((installment, index) =>
                    (() => {
                      const displayStatus = getInstallmentStatusLabel(
                        installment?.status,
                        selectedPlan?.active
                      )
                      const displayDueDate = getInstallmentDueDate(
                        installment?.dueDate,
                        installment?.status,
                        selectedPlan?.active
                      )
                      return (
                        <tr
                          className="md-x md-x--scroll"
                          key={`${selectedPlan.id}-${index}`}
                        >
                          <td className="text-left">{index + 1}</td>
                          <td className="text-left">
                            {formatDate(displayDueDate)}
                          </td>
                          <td className="text-left">
                            {formatCurrency(installment?.amount)}
                          </td>
                          <td className="text-left">
                            {statusLabel(displayStatus)}
                          </td>
                        </tr>
                      )
                    })()
                  )}
                  {(!selectedPlan.installments ||
                    selectedPlan.installments.length === 0) && (
                    <tr className="md-x md-x--scroll">
                      <td colSpan={4} className="text-center">
                        No installments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Stack>
        </Card>
      )}
    </section>
  )
}
